import React from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { SidebarProvider, useSidebar } from "@/context/SidebarContext";
import {
  LayoutDashboard,
  BookUser,
  LayoutTemplate,
  Tag,
  Radio,
  BarChart3,
  IndianRupee,
  Megaphone,
  Briefcase,
  Receipt,
  Settings as SettingsIcon,
  Workflow,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Plus,
  Check,
  ExternalLink,
  UserRound,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import NotificationBell from "@/components/NotificationBell";
import DueAlerts from "@/components/DueAlerts";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import InstagramIcon from "@/components/InstagramIcon";

const CRM_URL = "https://crm.adiyashgym.in";

const OWNER_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, color: "#3B82F6" },
  { to: "/wa-chat", label: "WA Chat", icon: WhatsAppIcon },
  {
    label: "Meta Business", icon: Briefcase, color: "#1877F2",
    children: [
      { to: "/meta-billing", label: "Meta Billing", icon: IndianRupee, color: "#EAB308" },
      { to: "/meta-ads", label: "Meta Ads", icon: Megaphone, color: "#1877F2" },
    ],
  },
  { to: "/instagram-chat", label: "Instagram Chat", icon: InstagramIcon },
  { to: "/flows", label: "Flow Builder", icon: Workflow, color: "#A855F7" },
  { to: "/contacts", label: "Contacts", icon: BookUser, color: "#14B8A6" },
  { to: "/tags", label: "Tags", icon: Tag, color: "#F59E0B" },
  { to: "/templates", label: "Templates", icon: LayoutTemplate, color: "#6366F1" },
  { to: "/broadcasts", label: "Broadcasts", icon: Radio, color: "#EF4444" },
  { to: "/reports", label: "Reports", icon: BarChart3, color: "#22C55E" },
  { to: "/bill-tracking", label: "Bill Tracking", icon: Receipt, color: "#F97316" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, color: "#94A3B8" },
];

const STAFF_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, color: "#3B82F6" },
  { to: "/wa-chat", label: "WA Chat", icon: WhatsAppIcon },
  { to: "/instagram-chat", label: "Instagram Chat", icon: InstagramIcon },
  { to: "/reports", label: "Reports", icon: BarChart3, color: "#22C55E" },
];

function NavItem({ to, label, icon: Icon, color, collapsed, indent }) {
  return (
    <NavLink
      to={to}
      title={collapsed ? label : undefined}
      data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md text-base font-bold transition-all duration-150 ${
          collapsed ? "justify-center px-0 py-2.5" : indent ? "pl-8 pr-4 py-2.5" : "px-4 py-2.5"
        } ${
          isActive
            ? `text-white bg-white/10 border-l-4 border-orange-600 ${indent && !collapsed ? "pl-7" : collapsed ? "" : "pl-3"}`
            : "text-slate-400 hover:text-white hover:bg-white/5 hover:translate-x-0.5"
        }`
      }
    >
      <Icon className="h-[22px] w-[22px] shrink-0" style={color ? { color } : undefined} />
      {!collapsed && label}
    </NavLink>
  );
}

// A menu heading that opens and closes a short list of pages (e.g. Meta Business).
function NavGroup({ label, icon: Icon, color, children, collapsed, pathname }) {
  const hasActive = children.some((c) => pathname.startsWith(c.to));
  const [open, setOpen] = React.useState(hasActive);
  if (collapsed) {
    return children.map((c) => <NavItem key={c.to} {...c} collapsed />);
  }
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid={`nav-group-${label.toLowerCase().replace(/\s+/g, "-")}`}
        className={`w-full flex items-center gap-3 rounded-md text-base font-bold px-4 py-2.5 transition-all duration-150 hover:bg-white/5 hover:text-white ${
          hasActive ? "text-white" : "text-slate-400"
        }`}
      >
        <Icon className="h-[22px] w-[22px] shrink-0" style={color ? { color } : undefined} />
        <span className="flex-1 text-left">{label}</span>
        <ChevronDown className={`h-4 w-4 transition-transform duration-150 ${open ? "" : "-rotate-90"}`} />
      </button>
      {open && (
        <div className="mt-1 space-y-1">
          {children.map((c) => <NavItem key={c.to} {...c} indent />)}
        </div>
      )}
    </div>
  );
}

function LayoutInner() {
  const { user, logout } = useAuth();
  const { branches, activeBranch, setActiveBranchId } = useBranch();
  const { collapsed, toggle } = useSidebar();
  const navigate = useNavigate();
  const location = useLocation();
  const isOwner = user?.role === "owner";
  const NAV = isOwner ? OWNER_NAV : STAFF_NAV;

  const sidebarWidth = collapsed ? "w-[72px]" : "w-64";
  const contentOffset = collapsed ? "left-[72px]" : "left-64";
  const mainOffset = collapsed ? "ml-[72px]" : "ml-64";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="App bg-[#050505] min-h-screen">
      {/* Sidebar */}
      <aside
        className={`${sidebarWidth} fixed inset-y-0 left-0 z-50 bg-[#0a0a0a] flex flex-col border-r border-white/10 transition-[width] duration-300 ease-in-out`}
      >
        <div className="px-3 pt-4 pb-3 border-b border-white/10 shrink-0 space-y-3">
          <div className={`flex items-center gap-2 ${collapsed ? "justify-center" : "justify-between"}`}>
            {!collapsed && (
              <p className="font-heading font-black text-white text-lg leading-none truncate px-1">
                Adiyash Gym
              </p>
            )}
            <button
              onClick={toggle}
              title={collapsed ? "Expand sidebar" : "Collapse sidebar"}
              data-testid="sidebar-collapse-toggle"
              className="h-9 w-9 shrink-0 flex items-center justify-center rounded-md text-slate-400 hover:text-white hover:bg-white/5 active:scale-[0.97] transition-all duration-150"
            >
              {collapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-1">
          {NAV.map((item) =>
            item.children ? (
              <NavGroup key={item.label} {...item} collapsed={collapsed} pathname={location.pathname} />
            ) : (
              <NavItem key={item.to} {...item} collapsed={collapsed} />
            )
          )}
        </nav>
      </aside>

      {/* Topbar */}
      <header
        className={`h-16 fixed top-0 right-0 ${contentOffset} z-40 bg-[#0a0a0a] border-b border-white/10 flex items-center px-6 justify-between transition-[left] duration-300 ease-in-out`}
      >
        <div className="flex items-center gap-2">
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">
            Branch
          </span>
          {isOwner ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="branch-dropdown-trigger"
                  className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-white/10 hover:bg-white/5 transition-all duration-150 text-sm font-semibold text-white"
                >
                  {activeBranch ? activeBranch.name : "Select branch"}
                  {activeBranch?.display_phone_number && (
                    <span className="text-xs font-normal text-slate-500">{activeBranch.display_phone_number}</span>
                  )}
                  <ChevronDown className="h-4 w-4 text-slate-500" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="w-56">
                {branches.map((b) => (
                  <DropdownMenuItem
                    key={b.id}
                    data-testid={`branch-option-${b.id}`}
                    onClick={() => setActiveBranchId(b.id)}
                    className="flex items-center justify-between cursor-pointer"
                  >
                    <span className="flex flex-col">
                      <span>{b.name}</span>
                      {b.display_phone_number && (
                        <span className="text-xs font-normal text-slate-500">{b.display_phone_number}</span>
                      )}
                    </span>
                    {activeBranch?.id === b.id && (
                      <Check className="h-4 w-4 text-orange-600" />
                    )}
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="add-branch-menu-item"
                  onClick={() => navigate("/settings?section=branches")}
                  className="cursor-pointer text-orange-500 font-semibold focus:text-orange-400"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Branch
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span
              data-testid="branch-locked-label"
              className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-white/10 text-sm font-semibold text-white"
            >
              {activeBranch ? activeBranch.name : "—"}
              {activeBranch?.display_phone_number && (
                <span className="text-xs font-normal text-slate-400">{activeBranch.display_phone_number}</span>
              )}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          <DueAlerts />
          <a
            href={CRM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="go-to-crm-button"
            title="Go to CRM"
            className="flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-semibold text-white bg-orange-600 hover:bg-orange-700 active:scale-[0.97] transition-all duration-150"
          >
            <ExternalLink className="h-4 w-4 shrink-0" />
            Go to CRM
          </a>
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu-trigger"
                title="Account"
                className="h-9 w-9 rounded-full overflow-hidden flex items-center justify-center text-sm font-black text-primary-foreground bg-primary border border-white/10 hover:opacity-90 active:scale-95 transition-all duration-150"
              >
                {user?.avatar ? (
                  <img src={user.avatar} alt="" className="h-full w-full object-cover" />
                ) : (
                  (user?.name || user?.email || "?").charAt(0).toUpperCase()
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <div className="px-2 py-1.5">
                <p className="text-sm font-semibold text-white truncate">
                  {user?.name || "Unnamed"}
                </p>
                <p className="text-xs text-slate-500 truncate">
                  {isOwner ? "Owner" : "Staff"} · {user?.email}
                </p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                data-testid="profile-menu-item"
                onClick={() => navigate("/profile")}
                className="cursor-pointer"
              >
                <UserRound className="h-4 w-4 mr-2" />
                My Profile
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="logout-button"
                onClick={handleLogout}
                className="cursor-pointer text-red-400 focus:text-red-300"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Content */}
      <main className={`${mainOffset} pt-24 px-8 pb-8 min-h-screen bg-[#050505] transition-[margin] duration-300 ease-in-out`}>
        <div
          key={location.pathname}
          className="max-w-[1200px] mx-auto w-full animate-in fade-in slide-in-from-bottom-1 duration-300"
        >
          <Outlet />
        </div>
      </main>
    </div>
  );
}

export default function Layout() {
  return (
    <SidebarProvider>
      <LayoutInner />
    </SidebarProvider>
  );
}
